import { ContentBlockVMConfig, ContentBlockVMTheme } from 'components/contentBlock/contentBlock';
import { RecordingHelpers } from 'helpers/recording';
import { inject, injectable } from 'inversify';
import { Subscribable, SubscribableOrNullableValue } from 'knockout';
import { CurrentUserManager } from 'managers/currentUser';
import device from 'managers/device';
import IdeationManager from 'managers/ideation';
import { IWCCStorageManager } from 'managers/iStorage';
import { TopicManager } from 'managers/topic';
import VideoPresentationProgressManager from 'managers/videoPresentationProgress';
import { Topic } from 'models/topic';
import { wccModules } from '../../../enums/wccModules';
import './content.html';
import './content.scss';

components.preload('content-block');

const { discussionId } = settings;

export interface TopicContentVMConfig {
    topicId?: SubscribableOrNullableValue<string | undefined>
    blockTheme?: SubscribableOrNullableValue<ContentBlockVMTheme>
    showTitle?: SubscribableOrNullableValue<boolean>
}

@injectable()
export default class TopicContentVM {
    private topic: Subscribable<Topic | undefined>

    private blockTheme: Subscribable<ContentBlockVMTheme>

    private description: Subscribable<string>
    private cardsHelpText: Subscribable<string>
    private moderatorNotes: Subscribable<string>
    private videoPresentationInstructions: Subscribable<string>
    private ideationInstructions: Subscribable<string>
    private screenRecordingInstructions: Subscribable<string>

    private isTitleAllowed: Subscribable<boolean>

    topicId: Subscribable<string | undefined>

    contentBlocks = ko.pureComputed(() => this.getContentBlocks());

    constructor(
        @inject(wccModules.componentConfig) model: TopicContentVMConfig,
        @inject(wccModules.componentInfo) info: ko.components.ComponentInfo,
        @inject(wccModules.storage) storage: IWCCStorageManager
    ) {
        this.topicId = ko.flattenComputed(model.topicId);
        this.blockTheme = ko.flattenComputed(model.blockTheme, 'default');
        this.isTitleAllowed = ko.flattenComputed(model.showTitle, true);

        const userManager = storage.get(CurrentUserManager, { discussionId });
        const user = userManager.pluck(m => m.person);
        const isModeratorOrObserver = user.pluck(u => u.isModeratorOrObserver, false);

        const topicManager = storage.get(TopicManager, { topicId: this.topicId });
        this.topic = topicManager.pluck(m => m.topic);

        const vpProgressManager = storage.get(VideoPresentationProgressManager, { topicId: this.topicId });
        const isLoadingVPProgress = vpProgressManager.pluck(m => m.loading, true);
        const vpIsWatched = vpProgressManager.pluck(m => m.isWatched);
        const vpIsFinished = vpProgressManager.pluck(m => m.isFinished);

        const ideationManager = storage.get(IdeationManager, { topicId: this.topicId });

        this.description = this.topic.pluck(t => t.thoroughDescription, '');
        this.moderatorNotes = this.topic.pluck(t => isModeratorOrObserver() ? t.moderatorNotes : '', '');

        this.cardsHelpText = this.topic.pluck(topic => {
            if (topic.isCardSort() || topic.isCardScore())
                return device.isMobile() ? topic.cardSortHelpMobile() : topic.cardSortHelpDesktop();
        }, '');

        this.videoPresentationInstructions = this.topic.pluck(topic => {
            if (topic.isVideoPresentation()) {
                const vpOptions = topic.videoPresentationOptions();

                if (vpOptions != undefined) {
                    if (vpIsFinished())
                        return vpOptions.videoFinishedInstructions() ?? (vpIsWatched() ? (vpOptions.afterPlayedInstructions() ??  this.description) : this.description);

                    if (vpIsWatched())
                        return vpOptions.afterPlayedInstructions() ?? this.description;

                    return vpOptions.beforePlayedInstructions() ?? '';
                }
            }
        }, '');

        this.ideationInstructions = ko.pureComputed(() => {
            const topic = this.topic();
            const currentIdeationManager = ideationManager();

            if (topic != undefined && currentIdeationManager != undefined)
                if (currentIdeationManager.isIdeation()) {
                    if (currentIdeationManager.isRatingStage())
                        return topic.ideationRankingDescription() ?? '';
                    else
                        return topic.thoroughDescription() ?? '';
            }

            return '';
        });

        this.screenRecordingInstructions = this.topic.pluck(topic => {
            if (topic.isScreenRecording()) {
                const screenRecordingOptions = topic.screenRecordingOptions();

                if (screenRecordingOptions != undefined) {
                    if (device.isMobile())
                        return screenRecordingOptions.mobileRecordingInstructions();
                    else if (RecordingHelpers.checkIfScreenRecordingSupported() || isModeratorOrObserver())
                        return screenRecordingOptions.browserRecordingInstructions();
                    else
                        return screenRecordingOptions.nonCompatibleDevicesInstructions();
                }
            }
        }, '');

        const isReady = ko.pureComputed(() => {
            const topic = this.topic();
            let isReady = topic != undefined;

            if (topic != undefined && topic.isVideoPresentation())
                isReady = isReady && !isLoadingVPProgress();

            return isReady;
        });

        ko.applyBindingsToNode(info.element, {
            'visible': isReady,

            'css': {
                'topic-content': true
            }
        });
    }

    private getContentBlocks() {
        const topic = this.topic();
        const result: Array<ContentBlockVMConfig> = [];

        if (topic != undefined) {
            if (topic.isIdeation())
                result.push(this.getBlock({ content: this.ideationInstructions }));
            else if (topic.isVideoPresentation())
                result.push(this.getBlock({ content: this.videoPresentationInstructions }));
            else if (topic.isScreenRecording())
                result.push(this.getBlock({ content: this.screenRecordingInstructions }));
            else if( topic)
                result.push(this.getBlock({ content: this.description }));

            if (topic.isCardSort() || topic.isCardScore())
                result.push(this.getBlock({ content: this.cardsHelpText }));

            result.push(this.getBlock({
                title: this.isTitleAllowed.pluck(isTitleAllowed => isTitleAllowed ? labels.ModeratorNotes : undefined),
                topicId: this.topicId,
                content: this.moderatorNotes
            }));
        }

        return result.filter(b => (ko.unwrap(b.content) ?? '').length > 0);
    }

    private getBlock(config: Omit<ContentBlockVMConfig, 'theme'>): ContentBlockVMConfig {
        return {
            ...config,

            theme: this.blockTheme
        }
    }
}